import React, { CSSProperties } from 'react';
import * as styles from './GridList.css';
import { useBreakpoints } from '@design-system/breakpointsStore';
import { Typography } from '@design-system/components/typography/typography';
import { ListCellRenderer, ListCell } from './ListCell';

export type ListRow = {
  id: string;
  tooltip?: string;
  cells: Array<ListCell>;
  style?: React.CSSProperties;
  onClick?: () => void;
};

export type SchemaCell = {
  content: React.ReactElement | string;
  width: string | number;
};

export type GridListSchema = Array<SchemaCell>;

export const toGridListSchema = <T extends string = string>(
  rows: [T, SchemaCell['width']][],
): GridListSchema =>
  rows.map(([content, width]): SchemaCell => ({ content, width }));

export type GridListProps = {
  schema: GridListSchema;
  rows: Array<ListRow>;
  variant?: 'big' | 'small';
};

const GridListHeader = (p: GridListProps & { style: CSSProperties }) => (
  <div
    className={`${styles.grid} ${styles.gridHeader} ${
      p.variant === 'small' ? styles.gridHeaderSmall : ''
    }`}
    style={p.style}
    data-testid="grid-header"
  >
    {p.schema.map((v, i) => (
      <div key={i} className={styles.col} data-testid={`header-${i}`}>
        {typeof v.content === 'string' ? (
          <Typography variant="body" size="small">
            {v.content}
          </Typography>
        ) : (
          v.content
        )}
      </div>
    ))}
  </div>
);

const RenderListRow = (
  p: ListRow & { style: CSSProperties; variant?: GridListProps['variant'] },
) => {
  const cl = `${styles.grid} ${styles.gridRow} ${
    p.variant === 'small' ? styles.gridRowSmall : ''
  } ${p.onClick ? styles.clickable : ''}`;

  return (
    <div
      className={cl}
      key={p.id}
      style={p.style}
      data-testid={`row-${p.id}`}
      onClick={p.onClick}
    >
      {p.cells.map((c, i) => (
        <div key={i} className={styles.col} data-testid={`cell-${p.id}-${i}`}>
          <ListCellRenderer data={c} />
        </div>
      ))}
    </div>
  );
};

export const GridList = (p: GridListProps) => {
  const br = useBreakpoints();
  const gridTemplateColumns = br.mobile
    ? '1fr'
    : p.schema
        .map((s) =>
          br.tablet
            ? '1fr'
            : typeof s.width === 'number'
            ? s.width + 'px'
            : s.width,
        )
        .join(' ');

  return (
    <div className={styles.gridWrapper} data-testid="grid-list">
      <GridListHeader {...p} style={{ gridTemplateColumns }} />
      <div data-testid="grid-rows">
        {p.rows.map((v) => (
          <RenderListRow
            {...v}
            style={{ gridTemplateColumns }}
            key={v.id}
            variant={p.variant}
          />
        ))}
      </div>
    </div>
  );
};
